import { Controller } from "@hotwired/stimulus"

// Autocomplete de endereço via Google Places (legado Autocomplete).
// Requer script Maps JS com libraries=places e credential google.maps_api_key.
const SUPPORTED_COUNTRIES = ["us", "br"]

export default class extends Controller {
  static targets = ["address", "city", "zipCode", "state"]
  static values = {
    states: Array,
    enabled: { type: Boolean, default: false },
  }

  connect() {
    if (!this.enabledValue) return
    this.boundInit = this.initAutocomplete.bind(this)
    if (window.google?.maps?.places) {
      this.initAutocomplete()
    } else {
      window.addEventListener("google-maps-ready", this.boundInit, { once: true })
    }
  }

  disconnect() {
    window.removeEventListener("google-maps-ready", this.boundInit)
    if (this.autocomplete) {
      window.google?.maps?.event?.clearInstanceListeners?.(this.autocomplete)
      this.autocomplete = null
    }
  }

  initAutocomplete() {
    if (!this.hasAddressTarget || !window.google?.maps?.places) return
    if (this.autocomplete) return

    this.autocomplete = new window.google.maps.places.Autocomplete(this.addressTarget, {
      fields: ["address_components", "formatted_address", "name"],
      types: ["address"],
      componentRestrictions: { country: SUPPORTED_COUNTRIES },
    })

    this.autocomplete.addListener("place_changed", () => this.fillFromPlace())
  }

  fillFromPlace() {
    const place = this.autocomplete.getPlace()
    if (!place?.address_components) return

    const components = place.address_components
    const get = (type, useShort = false) => {
      const match = components.find((c) => c.types.includes(type))
      if (!match) return ""
      return useShort ? match.short_name : match.long_name
    }

    const countryCode = get("country", true).toLowerCase()
    if (!SUPPORTED_COUNTRIES.includes(countryCode)) return

    const streetNumber = get("street_number")
    const route = get("route")
    const address = [streetNumber, route].filter(Boolean).join(" ") || place.formatted_address || ""
    const city =
      get("locality") ||
      get("sublocality") ||
      get("administrative_area_level_2") ||
      get("postal_town")
    const zip = get("postal_code")
    const stateCode = get("administrative_area_level_1", true)

    if (this.hasAddressTarget) {
      this.addressTarget.value = address
      this.addressTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
    if (this.hasCityTarget) {
      this.cityTarget.value = city
      this.cityTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
    if (this.hasZipCodeTarget) {
      this.zipCodeTarget.value = zip
      this.zipCodeTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }

    this.matchState(stateCode, countryCode)
  }

  matchState(stateCode, countryCode) {
    if (!this.hasStateTarget || !stateCode || !countryCode) return

    const states = this.statesValue || []
    const normalizedCode = stateCode.toUpperCase()
    const match = states.find(
      (s) => s.code?.toUpperCase() === normalizedCode && s.country === countryCode
    )

    if (!match) return

    this.stateTarget.value = String(match.id)
    this.stateTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }
}
